import styles from './styles.module.css';
import React, { useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid, Button } from '@mui/material';
import ShopSettingsService from 'services/ShopSettingsService';
import { STORAGE_KEYS, ROUTES, BACKEND_URL, PROD_BACKEND_BASE_URL, PROD_FE_BASE_URL } from 'const';
import SubscriptionService from 'services/SubscriptionService';
import { AlertMessagesContext } from 'react-alert-messages';
import KeyRenewPopup from 'components/popups/KeyRenewPopup';
import ShopDeleteConfirmationPopup from 'components/popups/ShopDeleteConfirmationPopup';
import { ShopContext } from 'Context/ShopContext';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';

export default function ShopProfile() {
  const { t: tCommon } = useTranslation('common');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { setShop } = useContext(ShopContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [profile, setProfile] = useState('');
  const [showKeyRenewalPopup, setShowKeyRenewalPopup] = useState(false);
  const [showDeleteShopConfirmationPopup, setShowDeleteShopConfirmationPopup] = useState(false);

  const history = useHistory();

  const loadShopProfile = async () => {
    setLoadingIndicator(true);
    const resp = await ShopSettingsService.loadShopProfile();
    setProfile(resp);
    setLoadingIndicator(false);
  };

  const clearAndReloadShopProfile = async () => {
    const resp = await ShopSettingsService.clearAndReloadShopProfile();
    setProfile(resp);
    setShop(resp);
  };

  const handleSubscriptionRenewalWithKey = async () => {
    setLoadingIndicator(true);
    try {
      const secret = localStorage.getItem(STORAGE_KEYS.REGISTRSATION_KEY);
      await SubscriptionService.renewSubscriptionWithKey({ secret });
      await clearAndReloadShopProfile();
      setShowKeyRenewalPopup(false);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  useEffect(() => {
    loadShopProfile();
  }, []);

  return (
    <>
      <div className={styles.contentLayout}>
        <div className={styles.settingsGrid}>
          <div style={{ marginBottom: 15 }}>
            <span className={styles.pageHead}>{tCommon('Shop details of {{name}}', { name: profile.shop_name })}</span>
          </div>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} className={styles.BillHeader}>
              <Grid item xs={12} sm={12} className={styles.fieldHead}>
                <span className={styles.fieldTitleSmall}>{tCommon('Shop Name')}</span>
              </Grid>
              <Grid item xs={12} sm={12} className={styles.fieldInput}>
                <span className={styles.fieldTitle}>
                  {loadingIndicator ? <Skeleton width="200px" animation="wave" /> : profile.shop_name}
                </span>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} className={styles.BillHeader}>
              <Grid item xs={12} sm={12} className={styles.fieldHead}>
                <span className={styles.fieldTitleSmall}>{tCommon('Mobile No')}</span>
              </Grid>
              <Grid item xs={12} sm={12} className={styles.fieldInput}>
                <span className={styles.fieldTitle}>
                  {loadingIndicator ? <Skeleton width="150px" animation="wave" /> : profile.mobile_no}
                </span>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} className={styles.BillHeader}>
              <Grid item xs={12} sm={12} className={styles.fieldHead}>
                <span className={styles.fieldTitleSmall}>{tCommon('Subscription Type')}</span>
              </Grid>
              <Grid item xs={12} sm={12} className={styles.fieldInput}>
                <span className={styles.fieldTitle}>
                  {loadingIndicator ? <Skeleton width="100px" animation="wave" /> : profile.subscription?.name}
                </span>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} className={styles.BillHeader}>
              <Grid item xs={12} sm={12} className={styles.fieldHead}>
                <span className={styles.fieldTitleSmall}>{tCommon('Subscription Period')}</span>
              </Grid>
              <Grid item xs={12} sm={12} className={styles.fieldInput}>
                <span className={styles.fieldTitle}>
                  {loadingIndicator ? (
                    <Skeleton width="80px" animation="wave" />
                  ) : (
                    tCommon('{{days}} Days', { days: profile.subscription?.days })
                  )}
                </span>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} className={styles.BillHeader}>
              <Grid item xs={12} sm={12} className={styles.fieldHead}>
                <span className={styles.fieldTitleSmall}>{tCommon('Subscription Validity')}</span>
              </Grid>
              <Grid item xs={12} sm={12} className={styles.fieldInput}>
                <span className={styles.fieldTitle}>
                  {loadingIndicator ? <Skeleton width="120px" animation="wave" /> : profile.subscription_end_date}
                </span>
              </Grid>
            </Grid>
            {(profile.image_support_validity || loadingIndicator) && (
              <Grid item xs={12} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>{tCommon('Image Support Validity')}</span>
                </Grid>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {loadingIndicator ? <Skeleton width="120px" /> : profile.image_support_validity}
                  </span>
                </Grid>
              </Grid>
            )}
            {(profile.kot_validity || loadingIndicator) && (
              <Grid item xs={12} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>{tCommon('KOT Validity')}</span>
                </Grid>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {loadingIndicator ? <Skeleton width="120px" /> : profile.kot_validity}
                  </span>
                </Grid>
              </Grid>
            )}
            {(profile.kot_lite_validity || loadingIndicator) && (
              <Grid item xs={12} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>{tCommon('KOT lite Validity')}</span>
                </Grid>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {loadingIndicator ? <Skeleton width="120px" /> : profile.kot_lite_validity}
                  </span>
                </Grid>
              </Grid>
            )}
            {(profile.expense_validity || loadingIndicator) && (
              <Grid item xs={12} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>{tCommon('Additional Expense Validity')}</span>
                </Grid>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {loadingIndicator ? <Skeleton width="120px" /> : profile.expense_validity}
                  </span>
                </Grid>
              </Grid>
            )}
            {(profile.smart_scale_validity || loadingIndicator) && (
              <Grid item xs={12} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>{tCommon('Smart Scale Validity')}</span>
                </Grid>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {loadingIndicator ? <Skeleton width="120px" /> : profile.smart_scale_validity}
                  </span>
                </Grid>
              </Grid>
            )}
            {(profile.customer_support_validity || loadingIndicator) && (
              <Grid item xs={12} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>{tCommon('Customer Support Validity')}</span>
                </Grid>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {loadingIndicator ? <Skeleton width="120px" /> : profile.customer_support_validity}
                  </span>
                </Grid>
              </Grid>
            )}
            {(profile.bluetooth_scale_validity || loadingIndicator) && (
              <Grid item xs={12} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>{tCommon('Bluetooth Support Validity')}</span>
                </Grid>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {loadingIndicator ? <Skeleton width="120px" /> : profile.bluetooth_scale_validity}
                  </span>
                </Grid>
              </Grid>
            )}
            {(profile.quickbook_support_validity || loadingIndicator) && (
              <Grid item xs={12} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>{tCommon('External API Support Validity')}</span>
                </Grid>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {loadingIndicator ? (
                      <Skeleton width="120px" animation="wave" />
                    ) : (
                      profile.quickbook_support_validity
                    )}
                  </span>
                </Grid>
              </Grid>
            )}
            {(profile.warehouse_validity || loadingIndicator) && (
              <Grid item xs={12} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>{tCommon('Warehouse Validity')}</span>
                </Grid>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {loadingIndicator ? <Skeleton width="120px" animation="wave" /> : profile.warehouse_validity}
                  </span>
                </Grid>
              </Grid>
            )}
            {(loadingIndicator || Number(profile.ai_points ?? 0) > 0) && (
              <Grid item xs={12} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>{tCommon('AI Credit Point')}</span>
                </Grid>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {loadingIndicator ? (
                      <Skeleton width="120px" animation="wave" />
                    ) : (
                      profile.ai_points ?? 0
                    )}
                  </span>
                </Grid>
              </Grid>
            )}



            {/* <Grid item xs={6} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>
                    Subscription Rate
                  </span>
                </div>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    ₹ {subscription.rate}
                  </span>
                </div>
              </Grid>
              <Grid item xs={6} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>
                    Maximum Products
                  </span>
                </div>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {subscription.max_products === -1
                      ? 'Unlimited Products'
                      : `${subscription.max_products} Products`}
                  </span>
                </div>
              </Grid>
              <Grid item xs={6} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>
                    Maximum Machines
                  </span>
                </div>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {subscription.max_machines === -1
                      ? 'Upto 5 Machines'
                      : `${subscription.max_machines} Machine`}
                  </span>
                </div>
              </Grid>
              <Grid item xs={6} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>Product Sync</span>
                </div>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {subscription.auto_sync_product_save === true
                      ? 'Automatic Product Sync to Machine '
                      : ` Manual Product Sync to Machine`}
                  </span>
                </div>
              </Grid>
              <Grid item xs={6} sm={12} className={styles.BillHeader}>
                <Grid item xs={12} sm={12} className={styles.fieldHead}>
                  <span className={styles.fieldTitleSmall}>Sales Sync</span>
                </div>
                <Grid item xs={12} sm={12} className={styles.fieldInput}>
                  <span className={styles.fieldTitle}>
                    {subscription.sales_sync_interval === -1
                      ? ' Automatic Sales Updates'
                      : ` Sales Update in ${subscription.sales_sync_interval} Hours`}
                  </span>
                </div>
              </Grid> */}
            <div style={{ marginBottom: 10, marginTop: 20 }}>
              <span className={styles.pageHead}>{tCommon('Renew Subscription')}</span>
            </div>
          </Grid>
          <Grid container spacing={1} justifyContent="space-between">
            <Grid item spacing={1} style={{ display: 'flex' }}>
              {!profile.dealer || profile.dealer?.is_online_renewal_enabled ? (
                (BACKEND_URL !== PROD_BACKEND_BASE_URL || window.location.origin === PROD_FE_BASE_URL) && (
                  <Grid item>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        history.push(ROUTES.RENEW_PLAN);
                      }}
                      style={{ marginRight: 10 }}
                    >
                      {tCommon('RENEW ONLINE')}
                    </Button>
                  </Grid>
                )
              ) : profile.dealer?.customer_support_number ? (
                <Grid item>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      const message = `${profile?.shop_name} (${profile?.id})\nneed to renew subscription`;
                      window.open(
                        `https://wa.me/${profile?.dealer?.customer_support_number}?text=${encodeURIComponent(message)}`,
                        '_blank'
                      );
                    }}
                    style={{ marginRight: 10 }}
                  >
                    {tCommon('Contact Support', 'Contact Support')}
                  </Button>
                </Grid>
              ) : null}

              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setShowKeyRenewalPopup(true);
                  }}
                >
                  {tCommon('RENEW WITH COUPON')}
                </Button>
              </Grid>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setShowDeleteShopConfirmationPopup(true);
                }}
              >
                {tCommon('DELETE SHOP')}
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>

      {showKeyRenewalPopup && (
        <KeyRenewPopup
          handleClose={() => setShowKeyRenewalPopup(false)}
          handleKeyRegister={handleSubscriptionRenewalWithKey}
        />
      )}

      {showDeleteShopConfirmationPopup && (
        <ShopDeleteConfirmationPopup shop={profile} handleClose={() => setShowDeleteShopConfirmationPopup(false)} />
      )}
    </>
  );
}
